import { html } from 'lit/html.js'
import { styleMap } from 'lit/directives/style-map.js';
import { until } from 'lit/directives/until.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { StateElement } from '../unit-base'

const signature = `
Element will load and cache provided svg asset. During loading a placeholder
with the corresponding width and height is set.

### Set State with Attributes and Properties
\`\`\`javascript
document.querySelector("unit-svg").state = {
  id: null,
  src: "icon.svg",
  hidden: null,
  disabled: null,
  // Properties
  background: "none",
  padding: "2px",
  // If set to pointer element will emit action
  // Else click will bubble with target unit-svg
  cursor: "default",
  // will not respect aspectRatio
  distort: true
  fill: "no default value",
  stroke: "no default value"
}

\`\`\`

### Events
\`\`\`javascript
document.querySelector("unit-svg").addEventListener("action", (e) => {
  // Merged Attributes and Properties
  console.log(e.target.state)
})
\`\`\`
`

export default class SVG extends StateElement {

  static get signature() {
    return signature.trim()
  }

  static get observedAttributes() {
    return [ "id", "src", "hidden", "disabled", "fill", "href", "cursor", "height", "width", "distort" ]
  }

  connectedCallback() {
    // If no attributes and properties are set
    this.render()
    // Use target => this for further propagation
    this.onclick = ( e ) => {
      if ( e.target != this ) {
        e.stopPropagation()
        if ( this.state.cursor == "pointer" ) {
          this.dispatchEvent( new CustomEvent( 'action', { 'bubbles': true } ) )
        } else {
          this.dispatchEvent( new CustomEvent( 'click', { 'bubbles': true } ) )
        }
      }
    }
  }

  template( data ) {

    const style = {
      width: data.width || data.height || "1.4rem",
      height: data.height || data.width || "1.4rem",
      background: data.background || "none",
      padding: data.padding || "0",
      cursor: data.cursor || "default",
      fill: data.fill,
      stroke: data.stroke
    }

    return until( load( style, !!data.distort, data.src ), load( style, !!data.distort ) )
  }

}

// -----------------------------------
// Store Directive function to prevent
// refetch of resources
// -----------------------------------
const proxy = {}

async function load( style, distort, src ) {

  const placeholder = html `
      <svg viewBox="0 0 100 100" style="${styleMap(style)}"></svg>
    `

  // Provide Placeholder
  if ( !src ) {
    return placeholder
  }

  // Create temporary element
  const ele = document.createElement( "div" )

  // Cache result
  if ( !proxy[ src ] ) {
    try {
      ele.innerHTML = proxy[ src ] = await fetch( src ).then( res => res.text() )
    } catch ( e ) {
      console.log( "Couldn't load svg", e )
    }
  } else {
    ele.innerHTML = proxy[ src ]
  }

  // Normalize
  if ( ele.querySelector( "svg" ) ) {

    const svg = ele.querySelector( "svg" )
    const box = svg.getAttribute( "viewBox" ) || "0 0 100 100"
    const ratio = distort == true ? "none" : "xMidYMid meet"

    return html `
      <svg viewBox="${box}" style="${styleMap(style)}" preserveAspectRatio="${ratio}">
        ${ Array.from(svg.childNodes) }
      </svg>`
  }
  // Remove from cache
  else {
    delete proxy[ src ]
  }

  return placeholder

}